/**
 * PlanActionModal — customer-facing workflow execution modal for the plans page.
 *
 * When a user clicks a plan button (Subscribe, Upgrade, Downgrade, etc.),
 * this modal opens and shows:
 *   1. Plan details (name, price, what changes)
 *   2. Financial preview (proration, credits, etc.)
 *   3. WorkflowRunForm in customer mode (pre-filled, minimal input)
 *   4. Live execution progress
 *
 * For direct endpoints (free plan activation), skips the workflow form
 * and just shows a confirmation.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import WorkflowRunForm from './workflow/WorkflowRunForm';
import billingApi from '../services/billingApi';
import { isManualReview, MANUAL_REVIEW_CUSTOMER_TITLE, MANUAL_REVIEW_CUSTOMER_DETAIL } from '../utils/workflowStatus';

export default function PlanActionModal({ open, onClose, plan, action, onComplete }) {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(false);
  // 999.4741/999.4758/999.4760 BLOCK 5: a manual_review instance never
  // reaches "executed" (that would render "Done! Your plan has been
  // updated." for an unconfirmed, possibly money-moving change) but the
  // modal must still tell the user directly, at its own chrome level, that
  // their request is parked — not rely solely on the child WorkflowRunForm's
  // own CustomerResult rendering underneath.
  const [parkedForReview, setParkedForReview] = useState(false);

  // Extract workflow ID from the action endpoint
  const workflowMatch = action?.endpoint?.match(/\/api\/workflows\/(.+)\/execute/);
  const workflowId = workflowMatch?.[1];

  useEffect(() => {
    if (!open || !workflowId) {
      setDefinition(null);
      setError('');
      setExecuted(false);
      setParkedForReview(false);
      return;
    }
    loadWorkflow();
  }, [open, workflowId]);

  async function loadWorkflow() {
    setLoading(true);
    setError('');
    try {
      const data = await billingApi.get(`/api/workflows/${workflowId}`);
      setDefinition(data.definition);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // For direct endpoints (no workflow), execute immediately with confirmation
  async function handleDirectAction() {
    setExecuting(true);
    setError('');
    try {
      const result = await billingApi.post(action.endpoint, action.payload);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      setExecuted(true);
      setTimeout(() => {
        onComplete?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setExecuting(false);
    }
  }

  function handleWorkflowComplete(instance) {
    // Only close when the workflow actually reaches a terminal state.
    // Paused workflows (e.g., paused_for_input) should keep the modal open.
    if (!instance) return;

    // (999.4746 PAY-WORKFLOW-STATUS-CONTRACT: this array is a registered
    // consumer, mechanically checked as a literal named `terminalStates` at
    // THIS call site — keep it inline, never a shared imported constant.)
    const terminalStates = ['completed', 'failed', 'canceled', 'undone', 'manual_review'];
    if (terminalStates.includes(instance.status)) {
      // 999.4741/999.4758/999.4760: manual_review IS terminal (999.4741 — no
      // supported transition leads out of it), which is why it belongs in
      // terminalStates above (polling upstream has genuinely stopped), but
      // it is NEITHER success NOR failure: a Stripe refund/charge may
      // already have executed and is unverified. Do not close the modal or
      // call onComplete (onComplete closes the modal and reloads the plans
      // page's data as if the change had taken effect) — that would report
      // a parked, unconfirmed instance as a normal completion. Tell the
      // user directly instead, and leave the close (X) button live so they
      // can dismiss manually rather than wait on a state change that cannot
      // occur.
      if (isManualReview(instance.status)) {
        setParkedForReview(true);
        return;
      }

      setExecuted(true);
      setTimeout(() => {
        onComplete?.();
        onClose();
      }, 2000);
    }
    // Non-terminal (running, paused_for_input, paused_for_approval) — keep modal open
  }

  if (!plan || !action) return null;

  const isFree = plan.price_cents === 0;
  const isDirectAction = !workflowId;
  const price = plan.price_cents > 0
    ? `$${(plan.price_cents / 100).toFixed(2)}/${plan.interval_type === 'year' ? 'yr' : 'mo'}`
    : 'Free';

  return (
    <Dialog
      open={open}
      onClose={executed ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketLaunchIcon sx={{ color: '#C8942A' }} />
            <Typography variant="h6">
              {action.label}
            </Typography>
          </Box>
          {!executed && (
            <IconButton size="small" onClick={onClose} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Plan Summary */}
        <Box sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{plan.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {plan.description || `${plan.product_name} plan`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" color="primary" fontWeight={700}>{price}</Typography>
              {plan.trial_days > 0 && action.show_trial && (
                <Chip label={`${plan.trial_days}-day free trial`} size="small" color="success" />
              )}
            </Box>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {executed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Done! Your plan has been updated.
          </Alert>
        )}

        {parkedForReview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>{MANUAL_REVIEW_CUSTOMER_TITLE}</Typography>
            <Typography variant="caption">{MANUAL_REVIEW_CUSTOMER_DETAIL}</Typography>
          </Alert>
        )}

        {/* Direct action (free plan) — simple confirmation */}
        {isDirectAction && !loading && !executed && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {isFree ? (
              <Typography variant="body1" color="text.secondary">
                Activate your free plan to get started immediately.
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary">
                You'll be redirected to complete payment.
              </Typography>
            )}
          </Box>
        )}

        {/* Workflow action — show WorkflowRunForm */}
        {workflowId && loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {workflowId && definition && !loading && (
          <>
            <Divider sx={{ mb: 2 }} />
            <WorkflowRunForm
              definition={definition}
              mode="customer"
              prefilledData={{ ...(action.payload?.input_data || {}), ...(action.input_overrides || {}) }}
              onExecuted={handleWorkflowComplete}
              showProgress
              compact
            />
          </>
        )}
      </DialogContent>

      {/* Only show action buttons for direct actions */}
      {isDirectAction && !executed && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} disabled={executing}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDirectAction}
            disabled={executing}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}
          >
            {executing ? 'Processing...' : action.label}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
